import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { useService } from '../../../../services/services';
import { route } from '../../../../services/utils';
import {
  CROP_FIELD_HISTORY_VIEW,
  CROP_FIELD_HISTORY,
} from '../../../../router/routes';
import Field from '../../../../interfaces/field';
import {
  Tab,
  TabContainer,
  TabContents,
  TabLink,
  TabNavigation,
} from '../../../../components/common/tabs/tab-container';
import css from './view.module.css';
import { LinkComponent } from '../../../../components/form/elements/link';
import { faLongArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { TabKey as HistoryTabs } from '../index';
import { FieldHistoryTabs } from '../../../../enums/field-history-tabs';
import FieldHistoryWorkIndex from './work';
import FieldHistoryFertilizationIndex from './fertilization';
import FieldHistorySeedIndex from './seed';
import FieldHistoryCropProtectionIndex from './crop-protection';
import FieldHistoryYieldIndex from './yield';
import FieldHistoryDataIndex from './data';

export default function FieldHistoryView() {
  const { t } = useTranslation();

  const transKeyPrefix = 'pages.crop.field-history.history.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const history = useHistory();
  const { id, farmId, season, tab } = useParams<{
    id: string;
    farmId: string;
    season: string;
    tab: string;
  }>();
  const { fieldService } = useService();
  const [title, setTitle] = useState(null);
  const [currentTab, setTab] = useState(tab);

  useEffect(() => {
    const routeTo = route(CROP_FIELD_HISTORY_VIEW, {
      id: id,
      tab: currentTab,
      farmId: farmId,
      season: season,
    });

    if (routeTo !== history.location.pathname) {
      history.push(routeTo);
    }
  }, [history, farmId, season, id, currentTab]);

  useEffect(() => {
    const subscription = fieldService
      .show(farmId, id)
      .subscribe((field: Field) => {
        setTitle(field.label);
      });

    return () => subscription.unsubscribe();
  }, [fieldService, id, farmId]);

  return (
    <div className={css['field-info']}>
      <div className={css['header']}>
        <LinkComponent
          label="back"
          icon={faLongArrowLeft}
          linkTo={route(CROP_FIELD_HISTORY, {
            farmId: farmId,
            season: season,
            tab: HistoryTabs.HISTORY,
          })}
        />
        <h2>{title ?? ''}</h2>
      </div>

      <div className={css['content']}>
        <TabContainer
          selectedTabKey={currentTab}
          setTab={setTab}
          tabNavigation={
            <TabNavigation theme={'pages'}>
              <TabLink tabKey={FieldHistoryTabs.WORK} theme={'pages'}>
                {tp(`section.${FieldHistoryTabs.WORK}.title`)}
              </TabLink>
              <TabLink
                tabKey={FieldHistoryTabs.CROP_PROTECTION}
                theme={'pages'}
              >
                {tp(`section.${FieldHistoryTabs.CROP_PROTECTION}.title`)}
              </TabLink>
              <TabLink tabKey={FieldHistoryTabs.FERTILIZATION} theme={'pages'}>
                {tp(`section.${FieldHistoryTabs.FERTILIZATION}.title`)}
              </TabLink>
              <TabLink tabKey={FieldHistoryTabs.SEED} theme={'pages'}>
                {tp(`section.${FieldHistoryTabs.SEED}.title`)}
              </TabLink>
              <TabLink tabKey={FieldHistoryTabs.YIELD} theme={'pages'}>
                {tp(`section.${FieldHistoryTabs.YIELD}.title`)}
              </TabLink>
              <TabLink tabKey={FieldHistoryTabs.DATA} theme={'pages'}>
                {tp(`section.${FieldHistoryTabs.DATA}.title`)}
              </TabLink>
            </TabNavigation>
          }
          tabContents={
            <TabContents theme={'pages'}>
              <Tab tabKey={FieldHistoryTabs.WORK}>
                <FieldHistoryWorkIndex />
              </Tab>
              <Tab tabKey={FieldHistoryTabs.CROP_PROTECTION}>
                <FieldHistoryCropProtectionIndex />
              </Tab>
              <Tab tabKey={FieldHistoryTabs.FERTILIZATION}>
                <FieldHistoryFertilizationIndex />
              </Tab>
              <Tab tabKey={FieldHistoryTabs.SEED}>
                <FieldHistorySeedIndex />
              </Tab>
              <Tab tabKey={FieldHistoryTabs.YIELD}>
                <FieldHistoryYieldIndex />
              </Tab>
              <Tab tabKey={FieldHistoryTabs.DATA}>
                <FieldHistoryDataIndex />
              </Tab>
            </TabContents>
          }
        />
      </div>
    </div>
  );
}
